import React, { useState, useEffect, useCallback } from 'react';
import { useAvivaSelector } from '@redux/hook';
import { useHistory } from 'react-router-dom';
import { find, isEmpty, isNil } from 'lodash';
import { GAAviva } from '@utils/gaAviva';

import { SQSPocketResponse, Coverages } from '@models/MLTC/SQSPocketRequest.model';
import { MyInfoForm } from '@models/MLTC/MyInfo.model';
import { DeclarationForm } from '@models/MLTC/Declaration.model';
import { HealthForm } from '@models/MLTC/Health.model';

import Button from '@components/Button';
import IconPencil from '@assets/icon/pencil.svg';
import MultiSteps from '@components/MultiSteps';
import Heading from '@components/Heading';
import InformationBox from '@components/InformationBox';
import Accordion from '@components/Accordion';
import Link from '@components/Link';
import Listing from '@components/Listing';
import Bullet from '@components/Bullet';
import BulletList from '@components/BulletList';
import Text from '@components/Text';
import ContactBox from '@components/ContactBox';
import Modal from '@components/Modal';
import Divider from '@components/Divider';
import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import { Helpers } from '@utils/helpers';

const page = 'Review';

const importantNotes = [
  "There is a 60-day free-look period after you've received the policy contract.",
  'You may ask us to explain the product features to you.',
  'You may wish to separately seek advice on the suitability of the life policy.',
  'In the event that you choose not to seek advice on the suitability of the life policy, you should consider if the life policy is suitable for your financial circumstances and needs.',
]

interface IItem { text: string, value: string };

const trackOnload = (): void => {
  const GA = GAAviva.getInstance();
  let { form } = GA.digitalData;
  form.name = "Review";
  GA.trackPageLoad({ form });
}

export interface ReviewProps { }

function Review(props: ReviewProps) {
  console.log(`QnB ➤ PAGE ➤ Aviva - ${page}`);

  const history = useHistory();
  const contactInformation = useAvivaSelector(state => state.staticData.contactInformation);
  const basicDetails = useAvivaSelector(state => state.mltcStep1.basicDetails);
  const coverage = useAvivaSelector(state => state.mltcStep1.coverage);
  const quoteRawResponse = useAvivaSelector(state => state.mltcStep1.quoteRawResponse);
  const promoCode = useAvivaSelector(state => state.mltcStep1.appliedPromoCode);
  const myInfoForm = useAvivaSelector(state => state.mltcStep2.myInfoForm);
  const myInfoAddressForm = useAvivaSelector(state => state.mltcStep2.myInfoAddressForm);
  const income = useAvivaSelector(state => state.mltcStep3.income);
  const health = useAvivaSelector(state => state.mltcStep3.health);
  const isStep4Completed = useAvivaSelector(state => state.mltcStep4.isCompleted);
  const declaration = useAvivaSelector(state => state.mltcStep4.declaration);

  const [isShowConfirm, setIsShowConfirm] = useState<boolean>(false);
  const [planDetails, setPlanDetails] = useState<IItem[]>([]);
  const [personalDetails, setPersonalDetails] = useState<IItem[]>([]);
  const [otherDetails, setOtherDetails] = useState<IItem[]>([]);
  const [declarations, setDeclarations] = useState<IItem[]>([]);
  const [incomeQuestions, setIncomeQuestions] = useState<IItem[]>([]);
  const [healthQuestions, setHealthQuestions] = useState<IItem[]>([]);

  const campaignDetail = useAvivaSelector(state => state.loading.campaignDetail);
  const paramCC = campaignDetail?.campaignCode ? `?cc=${campaignDetail.campaignCode}` : '';
  const combineParamCC = campaignDetail?.campaignCode ? `&cc=${campaignDetail.campaignCode}` : '';

  const onClickContinue = () => {
    history.push('/mltc-r2/payment' + paramCC)
  };
  const onBackMyInfo = (step: number = 1) => {
    history.push(`/mltc/my-info?step=${step}` + combineParamCC);
  }

  const gotoBasic = () => {
    history.push('/mltc-r2/basic-details' + paramCC);
  }

  const gotoChoosePlan= () => {
    history.push('/mltc-r2/choose-plan' + paramCC);
  }

  const gotoAboutYou = (step: number = 0) => {
    history.push(`/mltc/health?step=${step}`);
  }

  const gotoClaration = useCallback(() => {
    history.push('/mltc-r2/declaration' + paramCC);
  }, [history]);

  const onBack = () => {
    gotoClaration();
  }

  useEffect(() => {
    console.log(
      'QnB ➤ useEffect() ➤',
      {
        'step1Slice ➤ coverage': coverage,
        'step1Slice ➤ quoteRawResponse': quoteRawResponse,
        'step3Slice ➤ health': health,
      }
    );

    // if (!isStep4Completed && process.env.REACT_APP_INJECT_CHECK !== 'true') {
    //   gotoClaration();
    // }
    if (!isEmpty(basicDetails)) {
      const { month, day, year } = basicDetails?.dob || { month: '', day: '', year: '' };
      const stringDate = `${month}/${day}/${year}`;
      const age = Helpers.getAge(new Date(stringDate));
      if (quoteRawResponse) {
        const planQuotation = new SQSPocketResponse(quoteRawResponse, age);
        setPlanDetails(planQuotation.mltcPlanDetails);
        trackOnload();
      }
    }

    
    if (myInfoForm) {
      const info = new MyInfoForm(myInfoForm);
      info.extraAddress = myInfoAddressForm;
      console.log(info, myInfoForm);
      setPersonalDetails(info.reviewPersonalDetails);
      setOtherDetails(info.reviewOtherDetails);
    }

    if (declaration) {
      const declarationForm = new DeclarationForm(declaration);
      setDeclarations(declarationForm.reviewDeclaration);
    }

    const aboutYouHealth = new HealthForm(health);
    setHealthQuestions(aboutYouHealth.reviewHealth);

  }, [quoteRawResponse, myInfoForm, declaration, income, promoCode, health, myInfoAddressForm, gotoClaration, isStep4Completed])

  const getValue = (id: any) => {
    console.log(id);
    return id === 'Y'  ? 'Yes' : 'No';
  }

  return (
    <Container>
      <ContainerGrid>
        <FullGrid>
          <MultiSteps steps={5} step={4} label="Review" />
          <Heading margin="0 0 30px"> Review </Heading>
        </FullGrid>
        <LeftGrid>

          <InformationBox variant="error">
            <Text><b>WARNING: </b>Pursuant to Section 23(5) of the Insurance Act 1966, you are to disclose in this application form fully and faithfully all facts which you know or ought to know. Otherwise the insurance effected may be void.</Text>
          </InformationBox>

          <Accordion
            isExpanded
            title="Basic details"
            rightElement={<Link text="Edit" variant="button" icon={IconPencil} onClick={gotoBasic} />}
          >
            <Listing margin={0} sources={[
              {
                text: 'Smoker',
                value: getValue(health?.isSmoker)
              }
            ]} variant="alternate-color" />
          </Accordion>
          <Divider size={2} margin="0 0 30px" />
          <Accordion
            isExpanded
            title="Singlife CareShield Standard details"
            rightElement={<Link text="Edit" variant="button" icon={IconPencil} onClick={gotoChoosePlan} />}
          >
            <Listing margin={0} sources={planDetails} variant="alternate-color" />
          </Accordion>
          <Divider size={2} margin="0 0 30px" />

          <InformationBox hasClose>
            <Text>Personal details pulled from Myinfo can’t be edited. To amend, please proceed to <Link variant="button" onClick={() => { setIsShowConfirm(true) }} text="Myinfo"></Link></Text>
          </InformationBox>

          <Accordion title="Personal details retrieved from Myinfo" isExpanded>
            <Listing margin={0} sources={personalDetails} variant="alternate-color" />
          </Accordion>
          <Divider size={2} margin="0 0 20px" />

          <Accordion
            isExpanded
            title="Other details"
            rightElement={<Link text="Edit" onClick={() => { onBackMyInfo(1) }} variant="button" icon={IconPencil} />}
          >
            <Listing margin={0} sources={otherDetails} variant="alternate-color" />
          </Accordion>
          <Divider size={2} margin="0 0 20px" />

          <Accordion
            title="Health questions"
            rightElement={<Link text="Edit" onClick={() => { gotoAboutYou(1) }} variant="button" icon={IconPencil} />}
          >
            <Listing margin={0} sources={healthQuestions} variant="alternate-color" />
          </Accordion>
          <Divider size={1} margin="0 0 20px" />

          <Accordion title="Declaration"
            rightElement={<Link text="Edit" onClick={gotoClaration} variant="button" icon={IconPencil} />}
          >
            <Listing margin={0} sources={declarations} variant="alternate-color" />
          </Accordion>
          <Divider size={1} margin="0 0 20px" />


          <Accordion title="Important notes" isExpanded>
            <BulletList>
              {importantNotes.map((note: string, index: number) => (<Bullet key={`${index}`}>{note}</Bullet>))}
            </BulletList>
          </Accordion>
          <Divider size={4} margin="0 0 40px" />

          <Button onClick={onClickContinue} label="Continue" margin="0 0 20px 0" />
          <Button variant="secondary" onClick={onBack} label="Back" margin="0 0 40px" />

          <Modal show={isShowConfirm} onClose={() => setIsShowConfirm(false)}>
            <Heading margin="0 0 20px" marginTablet="0 0 30px" variant="sub">You will be redirected to Myinfo</Heading>
            <Text margin="0 0 20px">Please note that by proceeding to edit your personal information in Myinfo, you are required to sign  in with your Singpass again. All information captured in this application will be removed.
            </Text>
            <Text margin="0 0 30px">Do allow time interval for your Myinfo profile to be updated before starting this application again.</Text>
            <Button margin="0 0 20px" label="Proceed" onClick={() => { onBackMyInfo(0) }}></Button>
            <Button margin={0} label="Cancel" variant="secondary" onClick={() => setIsShowConfirm(false)}></Button>
          </Modal>
        </LeftGrid>
        <EmptyGrid></EmptyGrid>
        {/* <RightGrid>
          <ContactBox items={contactInformation} />
        </RightGrid> */}
      </ContainerGrid>

    </Container>

  );
};

export default Review;
